import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, ScrollView, Text, useWindowDimensions, View } from 'react-native';
import { useNavigation, useRouter } from 'expo-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Toast from 'react-native-toast-message';
import Callout from '@/components/General/Callout';
import Input from '@/components/General/inputs/Input';
import SolidButton from '@/components/General/SolidButton';
import SheetModal from '@/components/General/SheetModal';
import SortableList from '@/components/General/SortableList';
import CutCard from '@/components/General/CutCard';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { saveServiceFlowTemplate } from '@/data/serviceFlows';
import { showAlert } from '@/utils/showAlert';
import usePlatform from '@/utils/hooks/usePlatform';
import { useServiceFlowDraft, isServiceFlowDraftDirty } from '@/utils/hooks/useServiceFlowDraft';
import {
    buildTemplateItemInput,
    flowItemEditorTitle,
    getTimingWarnings,
    invalidateServiceFlowTemplateQueries,
    isServiceFlowConflictError,
    isServiceFlowUpgradeError,
    newFlowItemID,
    validateFlowItem,
} from '@/utils/serviceFlow';
import { isServiceFlowV2Available, SERVICE_FLOW_LIMITS } from '@/config/serviceFlows';
import ServiceFlowTimeline from './ServiceFlowTimeline';
import ServiceFlowItemEditor from './ServiceFlowItemEditor';
import ServiceFlowItemTypePicker from './ServiceFlowItemTypePicker';
import { serviceFlowStyles } from './serviceFlowStyles';

const SIDE_PANEL_MIN_WIDTH = 960;

/**
 * Dedicated template workspace (spec §3.4). Timeline-first, single-item editor
 * in a side panel (wide) or SheetModal (narrow/native). Save item updates the
 * local draft; Save template publishes the whole draft atomically.
 */
const ServiceFlowTemplateWorkspace = ({ templateID = null }) => {
    const router = useRouter();
    const navigation = useNavigation();
    const queryClient = useQueryClient();
    const { isWeb } = usePlatform();
    const { width } = useWindowDimensions();

    // New drafts get a client UUID up front so p_template_id stays stable
    // across retries (expectedVersion=0 + existing UUID = conflict, not dupes).
    const { data: loggedInUser } = Query.people.loggedInUser();
    const organizationID = loggedInUser?.organizationID;
    const accountID = loggedInUser?.accountID;
    const { data: organization } = Query.organization.byId({ id: organizationID });
    const v2Available = isServiceFlowV2Available(organization);
    const canManage = !!loggedInUser?.can?.('Edit Events');

    const templateQuery = Query.serviceFlows.template({
        organizationID,
        accountID,
        templateID,
        options: { enabled: !!templateID && v2Available },
    });

    const draft = useServiceFlowDraft({
        organizationID,
        accountID,
        templateID,
        source: templateQuery.data,
        sourceLoading: templateQuery.isLoading,
    });
    const effectiveTemplateID = templateID || draft.id;
    const leaveApproved = useRef(false);

    // ---- Editor state: one item at a time ----
    const [pickerOpen, setPickerOpen] = useState(false);
    const [editing, setEditing] = useState(null); // { item, isNew }
    const [reorderOpen, setReorderOpen] = useState(false);

    const editingOriginal = useMemo(
        () => (draft.items || []).find(it => it.id === editing?.item?.id) || null,
        [draft.items, editing?.item?.id]
    );
    const editingDirty = useMemo(() => {
        if (!editing) return false;
        const baseline = editing.isNew
            ? editing.initial
            : (editing.initial ?? editingOriginal);
        return JSON.stringify(editing.item) !== JSON.stringify(baseline ?? editing.item);
    }, [editing, editingOriginal]);

    // ---- Guards ----
    // Route-leave / native back while the draft or an in-flight item edit is dirty.
    useEffect(() => {
        const unsub = navigation?.addListener?.('beforeRemove', (e) => {
            const dirty = isServiceFlowDraftDirty({ organizationID, accountID, templateID });
            if (leaveApproved.current || !(dirty || editingDirty)) return;
            e.preventDefault();
            showAlert({
                title: 'Discard unsaved changes?',
                subtitle: "You have changes to this service flow that haven't been saved.",
                isDestructive: true,
                confirmText: 'Discard',
                cancelText: 'Keep Editing',
                onConfirm: () => {
                    leaveApproved.current = true;
                    draft.clear();
                    navigation.dispatch(e.data.action);
                },
            });
        });
        return unsub;
    }, [navigation, organizationID, accountID, templateID, editingDirty, draft.clear]);

    useEffect(() => {
        if (!isWeb || typeof window === 'undefined' || !(draft.dirty || editingDirty)) return;
        const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [isWeb, draft.dirty, editingDirty]);

    const confirmCloseEditor = useCallback((afterDiscard) => {
        if (!editingDirty) {
            afterDiscard?.();
            return true;
        }
        showAlert({
            title: 'Discard unsaved changes?',
            subtitle: 'This item has changes that haven\'t been applied to the draft.',
            isDestructive: true,
            confirmText: 'Discard',
            cancelText: 'Keep Editing',
            onConfirm: () => afterDiscard?.(),
        });
        return false;
    }, [editingDirty]);

    const openEditorFor = useCallback((item) => {
        const proceed = () => {
            setPickerOpen(false);
            setEditing({ item: { ...item }, initial: { ...item }, isNew: false });
        };
        if (editing) {
            confirmCloseEditor(proceed);
        } else {
            proceed();
        }
    }, [editing, confirmCloseEditor]);

    const startAddItem = useCallback(() => {
        if ((draft.items || []).length >= SERVICE_FLOW_LIMITS.maxItems) {
            Toast.show({ type: 'error', text2: `Service flows are limited to ${SERVICE_FLOW_LIMITS.maxItems} items.` });
            return;
        }
        confirmCloseEditor(() => {
            setEditing(null);
            setPickerOpen(true);
        });
    }, [draft.items, confirmCloseEditor]);

    const handlePickType = useCallback((type) => {
        const item = draft.createDraftItem(type);
        setPickerOpen(false);
        setEditing({ item, initial: { ...item }, isNew: true });
    }, [draft]);

    const handleTypeChange = useCallback((nextType) => {
        if (!editing) return;
        const clearTask = { type: nextType, taskID: null, volunteerTeamID: null, Task: null, VolunteerTeam: null };
        if (editing.item.type === 'TASK' && editing.item.taskID) {
            showAlert({
                title: 'Change item type?',
                subtitle: 'Changing the type removes the attached task and team.',
                isDestructive: true,
                confirmText: 'Change Type',
                cancelText: 'Keep Task',
                onConfirm: () => setEditing(e => ({ ...e, item: { ...e.item, ...clearTask } })),
            });
        } else {
            setEditing(e => ({ ...e, item: { ...e.item, ...clearTask } }));
        }
    }, [editing]);

    const handleSaveItem = useCallback(() => {
        if (!editing) return;
        const item = editing.item;
        const errors = validateFlowItem(item);
        if (Object.keys(errors).length > 0) {
            Toast.show({ type: 'error', text2: Object.values(errors)[0] });
            return;
        }

        // Duplicate task/team slots are rejected server-side too — catch early
        // so the editor stays open with an inline explanation.
        if (item.type === 'TASK' && item.taskID) {
            const dupe = (draft.items || []).find(it =>
                it.id !== item.id && it.type === 'TASK' && it.taskID === item.taskID
                && (it.volunteerTeamID || null) === (item.volunteerTeamID || null)
            );
            if (dupe) {
                Toast.show({ type: 'error', text2: 'That task and team pair is already in this flow — pick a different task or team.' });
                return;
            }
        }

        draft.upsertItem(item);
        setEditing(null);
    }, [editing, draft]);

    const handleRemoveItem = useCallback((item) => {
        showAlert({
            title: `Remove "${item.title || 'this item'}"?`,
            subtitle: item.type === 'TASK'
                ? 'Future dates will stop getting this task when the template is saved.'
                : 'It will be removed from the template draft.',
            isDestructive: true,
            confirmText: 'Remove',
            onConfirm: () => {
                if (editing?.item?.id === item.id) setEditing(null);
                draft.removeItem(item.id);
            },
        });
    }, [draft, editing]);

    const handleDuplicateItem = useCallback((item) => {
        if ((draft.items || []).length >= SERVICE_FLOW_LIMITS.maxItems) {
            Toast.show({ type: 'error', text2: `Service flows are limited to ${SERVICE_FLOW_LIMITS.maxItems} items.` });
            return;
        }
        if (item.type === 'TASK') {
            confirmCloseEditor(() => {
                const copy = { ...item, id: newFlowItemID(), position: draft.items.length };
                setEditing({ item: copy, initial: { ...copy }, isNew: true });
                Toast.show({ type: 'info', text2: 'Pick a different task or team for the copy.' });
            });
        } else {
            draft.duplicateItem(item.id);
        }
    }, [draft, confirmCloseEditor]);

    // ---- Publish ----
    const saveMutation = useMutation({
        mutationFn: saveServiceFlowTemplate,
        onSuccess: (result) => {
            const saved = result?.template || {};
            queryClient.setQueryData(
                QueryKeys.serviceFlows.template(organizationID, accountID, saved.id || effectiveTemplateID),
                { ...saved, items: result.items }
            );
            draft.markSaved({
                version: saved.version,
                name: saved.name,
                description: saved.description,
                items: result?.items,
            });
            invalidateServiceFlowTemplateQueries(queryClient, {
                organizationID,
                accountID,
                templateID: saved.id || effectiveTemplateID,
            });

            const p = result?.propagation || {};
            const kept = (p.protected || 0) + (p.alreadyCurrent || 0);
            Toast.show({
                type: 'success',
                text2: `Template saved. ${p.refreshed ?? 0} upcoming date${p.refreshed === 1 ? '' : 's'} updated${kept ? `, ${kept} kept their own flow` : ''}.`,
            });

            if (!templateID && saved.id) {
                // New template — leave the draft keyed 'new', navigate to the
                // real workspace so the URL/resource identity match.
                draft.clear();
                router.replace(`/events/service-flow/${saved.id}`);
            }
        },
        onError: async (error) => {
            if (isServiceFlowConflictError(error)) {
                showAlert({
                    title: 'This template changed elsewhere',
                    subtitle: 'Someone else saved changes while you were editing. Reload the latest version? Your unsaved changes will be replaced.',
                    isDestructive: true,
                    confirmText: 'Reload latest',
                    cancelText: 'Keep my draft',
                    onConfirm: async () => {
                        await templateQuery.refetch();
                        draft.clear();
                    },
                });
                return;
            }
            if (isServiceFlowUpgradeError(error)) {
                Toast.show({ type: 'error', text2: 'Service Flow is being upgraded for your organization — try again shortly.' });
                return;
            }
            // ErrorHandler already toasted; keep the draft + editor intact.
        },
    });

    const handleSaveTemplate = () => {
        if (saveMutation.isPending || !canManage) return;
        if (editingDirty) {
            Toast.show({ type: 'error', text2: 'Save or cancel the open item before saving the template.' });
            return;
        }
        if (!(draft.name || '').trim()) {
            Toast.show({ type: 'error', text2: 'Give the template a name first.' });
            return;
        }
        saveMutation.mutate({
            templateID: effectiveTemplateID,
            expectedVersion: templateID ? draft.baselineVersion : 0,
            name: draft.name.trim(),
            description: draft.description?.trim() || null,
            items: (draft.items || []).map(buildTemplateItemInput),
        });
    };

    const handleDiscard = () => {
        if (!(draft.dirty || editingDirty)) return;
        showAlert({
            title: 'Discard unsaved changes?',
            subtitle: 'Your edits will be reverted to the last saved version.',
            isDestructive: true,
            confirmText: 'Discard',
            cancelText: 'Keep Editing',
            onConfirm: () => { setEditing(null); setPickerOpen(false); draft.discard(); },
        });
    };

    const reloadLatest = async () => {
        await templateQuery.refetch();
        setEditing(null);
        draft.clear();
    };

    // ---- Gates ----
    const availableContentWidth = width - (isWeb && width >= 1000 ? 260 : 0);
    const sideBySide = isWeb && availableContentWidth - 40 >= SIDE_PANEL_MIN_WIDTH;

    if (!v2Available) {
        return (
            <ScrollView style={serviceFlowStyles.pageContainer} contentContainerStyle={serviceFlowStyles.workspace}>
                <EmptyListPlaceholder
                    message="Service Flow templates are being rolled out"
                />
                <Text style={serviceFlowStyles.emptyBody}>
                    {`Template editing opens once your organization is upgraded. Your existing schedules keep working in the meantime.`}
                </Text>
            </ScrollView>
        );
    }

    if (templateID && templateQuery.isLoading) {
        return (
            <ScrollView style={serviceFlowStyles.pageContainer} contentContainerStyle={serviceFlowStyles.workspace}>
                <EmptyListPlaceholder message="Loading template…" />
            </ScrollView>
        );
    }

    if (templateID && (templateQuery.isError || (!templateQuery.isLoading && !templateQuery.data))) {
        return (
            <ScrollView style={serviceFlowStyles.pageContainer} contentContainerStyle={serviceFlowStyles.workspace}>
                <EmptyListPlaceholder message="This template isn't available" />
                <Text style={serviceFlowStyles.emptyBody}>
                    It may have been removed, or you might not have access to it.
                </Text>
                <SolidButton size="sm" type="events" variant="outlined" text="Try again" onPress={() => templateQuery.refetch()} />
            </ScrollView>
        );
    }

    const template = templateQuery.data;
    const warnings = getTimingWarnings(draft.items);

    const editorTitle = pickerOpen
        ? 'What kind of item?'
        : flowItemEditorTitle(editing?.item, editing?.isNew);

    const editorContent = (
        <>
            {pickerOpen && <ServiceFlowItemTypePicker onSelect={handlePickType} />}
            {!pickerOpen && editing && (
                <ServiceFlowItemEditor
                    key={editing.item.id}
                    item={editing.item}
                    onChange={(patch) => setEditing(e => ({ ...e, item: { ...e.item, ...patch } }))}
                    allowTypeChange
                    onTypeChange={handleTypeChange}
                    fromScreen={templateID ? `/events/service-flow/${templateID}` : '/events/service-flow/create'}
                    canCreateTasks={!!loggedInUser?.can?.('Create Tasks')}
                />
            )}
            {!pickerOpen && !editing && sideBySide && (
                <Text style={serviceFlowStyles.helperText}>
                    Select an item on the left to edit it, or add a new one.
                </Text>
            )}
        </>
    );

    const timelineHeader = (
        <View style={serviceFlowStyles.headerRow}>
            <View>
                <Text style={serviceFlowStyles.eyebrow}>Timeline</Text>
                {warnings.length > 0 && (
                    <Callout
                        type="warning"
                        style={{ marginTop: 8 }}
                        message={`${warnings[0]}${warnings.length > 1 ? ` (+${warnings.length - 1} more)` : ''}`}
                    />
                )}
            </View>
            {canManage && (
                <View style={serviceFlowStyles.headerActions}>
                    <SolidButton
                        size="sm"
                        type="events"
                        variant="muted"
                        iconName="add-outline"
                        iconBefore
                        text="Add item"
                        onPress={startAddItem}
                        testID="service-flow-add-item"
                    />
                    {(draft.items || []).length > 1 && (
                        <SolidButton
                            size="sm"
                            type="events"
                            variant="muted"
                            iconName="swap-vertical-outline"
                            iconBefore
                            text="Reorder"
                            onPress={() => setReorderOpen(true)}
                            testID="service-flow-reorder"
                        />
                    )}
                </View>
            )}
        </View>
    );

    return (
        <ScrollView style={serviceFlowStyles.pageContainer} contentContainerStyle={serviceFlowStyles.workspace}>
            {/* Header: name / description / status / publish actions */}
            <View style={serviceFlowStyles.headerRow}>
                <View style={{ flex: 1, minWidth: 240, gap: 10 }}>
                    <Text style={serviceFlowStyles.eyebrow}>Service Flow</Text>
                    <Input
                        title="Template name"
                        required
                        value={draft.name}
                        onUpdate={(v) => draft.setMeta({ name: v })}
                        disabled={!canManage}
                        testID="service-flow-template-name"
                    />
                    <Input
                        title="Description"
                        value={draft.description || ''}
                        onUpdate={(v) => draft.setMeta({ description: v })}
                        disabled={!canManage}
                        placeholder="Optional — shown in the template library"
                        testID="service-flow-template-description"
                    />
                </View>
                <View style={{ gap: 8, alignItems: 'flex-end' }}>
                    {draft.dirty && (
                        <View style={serviceFlowStyles.statusChip(true)}>
                            <Text style={serviceFlowStyles.statusChipText(true)}>Unsaved changes</Text>
                        </View>
                    )}
                    {!!template?.isArchived && (
                        <View style={serviceFlowStyles.statusChip(false)}>
                            <Text style={serviceFlowStyles.statusChipText(false)}>Archived</Text>
                        </View>
                    )}
                    {canManage && (
                        <View style={serviceFlowStyles.headerActions}>
                            <SolidButton
                                size="sm"
                                type="primary"
                                variant="outlined"
                                text="Discard"
                                disabled={!(draft.dirty || editingDirty) || saveMutation.isPending}
                                onPress={handleDiscard}
                                testID="service-flow-template-discard"
                            />
                            <SolidButton
                                size="sm"
                                type="events"
                                text="Save template"
                                disabled={!draft.dirty || editingDirty || saveMutation.isPending}
                                processing={saveMutation.isPending}
                                onPress={handleSaveTemplate}
                                testID="service-flow-template-save"
                            />
                        </View>
                    )}
                </View>
            </View>

            {template?.isArchived && (
                <Callout
                    type="warning"
                    message="This template is archived — it can't be attached to new events, but events already using it still receive it."
                />
            )}

            {draft.stale && (
                <Callout type="warning" message="This template changed elsewhere since you started editing.">
                    <Pressable onPress={reloadLatest} style={serviceFlowStyles.calloutAction}>
                        <Text style={serviceFlowStyles.calloutActionText}>Reload latest (replaces unsaved changes)</Text>
                    </Pressable>
                </Callout>
            )}

            {/* Timeline + editor */}
            <View style={sideBySide ? serviceFlowStyles.splitRow : undefined}>
                <View style={serviceFlowStyles.splitTimeline}>
                    <ServiceFlowTimeline
                        items={draft.items}
                        context="template"
                        header={timelineHeader}
                        canEdit={canManage}
                        selectedItemID={editing?.item?.id}
                        onPressItem={canManage ? openEditorFor : null}
                        onEditItem={openEditorFor}
                        onDuplicateItem={handleDuplicateItem}
                        onRemoveItem={handleRemoveItem}
                        onMoveItem={(item, dir) => draft.moveItem(item.id, dir)}
                        emptyState={
                            <EmptyListPlaceholder message={canManage
                                ? 'No items yet — add your first item'
                                : 'This template has no items'} />
                        }
                    />
                </View>

                {sideBySide && canManage && (
                    <View style={serviceFlowStyles.splitPanel}>
                        <CutCard accent={false} contentStyle={{ padding: 20 }}>
                            <View style={serviceFlowStyles.editorPanelHeader}>
                                <Text style={serviceFlowStyles.eyebrow}>{editorTitle}</Text>
                                {!pickerOpen && editing && (
                                    <View style={serviceFlowStyles.editorPanelActions}>
                                        <SolidButton
                                            size="sm"
                                            type="primary"
                                            variant="outlined"
                                            text="Cancel"
                                            onPress={() => confirmCloseEditor(() => setEditing(null))}
                                            testID="service-flow-item-cancel"
                                        />
                                        <SolidButton
                                            size="sm"
                                            type="events"
                                            text="Save item"
                                            onPress={handleSaveItem}
                                            disabled={Object.keys(validateFlowItem(editing.item)).length > 0}
                                            testID="service-flow-item-save"
                                        />
                                    </View>
                                )}
                            </View>
                            <View style={{ marginTop: 12 }}>{editorContent}</View>
                        </CutCard>
                    </View>
                )}
            </View>

            {/* Narrow/native: editor in a sheet */}
            {!sideBySide && (
                <SheetModal
                    isOpen={pickerOpen || !!editing}
                    title={editorTitle}
                    onCancel={() => confirmCloseEditor(() => { setEditing(null); setPickerOpen(false); })}
                    confirmText={!pickerOpen && editing ? 'Save item' : ''}
                    onConfirm={!pickerOpen && editing ? handleSaveItem : null}
                    width={760}
                >
                    {editorContent}
                </SheetModal>
            )}

            {/* Reorder — drag or Move earlier/later (accessible alternative lives
                in each row's menu). Reorder changes sequence only, never timing. */}
            <SortableList
                isOpen={reorderOpen}
                close={() => setReorderOpen(false)}
                title="Reorder items"
                helpText="Drag to change the order — times and durations are not affected."
                items={draft.items}
                itemHeight={48}
                itemContent={(item) => (
                    <Text style={{ fontSize: 14 }} numberOfLines={1}>{item.title || 'Untitled'}</Text>
                )}
                onConfirm={(ordered) => {
                    draft.setItems(ordered);
                    setReorderOpen(false);
                }}
            />
        </ScrollView>
    );
};

export default ServiceFlowTemplateWorkspace;
